import { Box } from '@mui/material';
import { fetchCreditLineEvaluations } from 'lib/api/decisionEngine';
import { EvaluationRow } from 'lib/api/decisionEngine/types';
import { EvaluationDetailDialog } from 'lib/common/EvaluationDetails';
import { Filter } from 'lib/common/GlobalSearcher';
import PageHeader from 'lib/designSystem/PageHeader';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { validateCompanyTypes, validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import NewEvaluation from './components/NewEvaluation';

const PAGE_SIZE = 25;

const CreditHubProEvaluations: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [openNewEvaluation, setOpenNewEvaluation] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [evaluationId, setEvaluationId] = useState<number>();
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [evaluations, setEvaluations] = useState<EvaluationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const canViewDetails = useMemo(
    () => validatePermissions(user, 'creditHubPro_evaluation_view_details'),
    [user],
  );

  const canCreateEvaluation = useMemo(
    () => validatePermissions(user, 'creditHubPro_evaluation_create'),
    [user],
  );

  const headers = useMemo(() => {
    const isFund = validateCompanyTypes(user, 'Fund');
    return [
      { key: 'business', label: 'Cliente', type: 'dual-text' },
      { key: 'label', label: 'Tipo de evaluación', type: 'text' },
      { key: 'engineName', label: 'Nombre motor' },
      { key: 'status', label: 'Resultado', type: 'status-chip' },
      { key: 'state', label: 'Estado', type: 'status-chip' },
      ...(isFund
        ? [{ key: 'origin', label: 'Origen', type: 'status-chip' }]
        : []),
      { key: 'createdAt', label: 'Fecha', type: 'date-large' },
    ];
  }, [user]);

  const filters: Filter[] = [
    {
      label: 'Nombre Cliente',
      type: 'autocomplete',
      key: 'businessName',
      value: name,
      setter: setName,
    },
    {
      label: 'Rut Cliente',
      type: 'autocomplete',
      key: 'businessIdentifier',
      value: identifier,
      setter: setIdentifier,
    },
  ];

  const searchData = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const response = await fetchCreditLineEvaluations(dispatch, {
        page,
        pageSize,
        identifier,
        name,
      });
      setEvaluations(response.data);
      setPagination(response.pagination);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const triggerSearch = async () => {
    await searchData({ page: 1, pageSize: pagination.pageSize });
  };

  useEffect(() => {
    void searchData({ page: 1, pageSize: PAGE_SIZE });
  }, []);

  const handleDetails = (id: number) => {
    setEvaluationId(id);
    setOpenDetails(true);
  };

  return (
    <>
      <Box>
        <PageHeader.WithTitleAndActions
          iconType="insights"
          title="Evaluaciones"
          subtitle="Aca podras revisar todas las evaluaciones que haz realizado.  "
          globalSearcherProps={{
            filters,
            triggerSearch,
            dispatch,
            disableSearch: loading,
          }}
          actionButtons={[
            {
              label: 'Nueva evaluación',
              onClick: () => setOpenNewEvaluation(true),
              disabled: !canCreateEvaluation,
            },
          ]}
        />
        <TableWithPagination
          dataHeaders={headers}
          data={evaluations}
          setData={setEvaluations}
          showActions
          handleClickRow={handleDetails}
          exportToFile="xlsx"
          lastElement
          loading={loading}
          pagination={pagination}
          setPagination={setPagination}
          searchData={searchData}
        />
      </Box>
      {openNewEvaluation && (
        <NewEvaluation
          type="creditLine"
          open={openNewEvaluation}
          setOpen={setOpenNewEvaluation}
        />
      )}
      {openDetails && canViewDetails && (
        <EvaluationDetailDialog
          evaluationId={evaluationId}
          open={openDetails}
          setOpen={setOpenDetails}
        />
      )}
    </>
  );
};

export default CreditHubProEvaluations;
